import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createCoachingPlaylist,
  deleteCoachingPlaylist,
  getCoachingPlaylists,
  updateCoachingPlaylist,
  type ApiError,
} from "@/lib/api";
import { invalidateScope, useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";
import type {
  CoachingPlaylist,
  CreateCoachingPlaylistRequest,
  UpdateCoachingPlaylistRequest,
} from "@/lib/types";

/**
 * Coach > Playlists list-page data hooks (M6a).
 *
 * Unlike Notes/Clips — whose create+edit route into Coach Review —
 * the **playlist composer is its own modal** (there's no Coach
 * Review surface for playlists). So `useCoachPlaylistMutations`
 * bundles all three writes: create / update / delete. Each
 * `invalidateScope`s on success so the list refetches.
 *
 * Scope-keyed + gated on `teamId`, like the other M6a list
 * queries.
 */

export function useCoachPlaylistsList() {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery({
    queryKey: useScopedKey("coaching-playlists"),
    queryFn: getCoachingPlaylists,
    enabled: !!teamId,
  });
}

export type CoachPlaylistMutations = {
  createPlaylist: (
    body: CreateCoachingPlaylistRequest,
  ) => Promise<{ playlist: CoachingPlaylist }>;
  updatePlaylist: (
    id: number,
    body: UpdateCoachingPlaylistRequest,
  ) => Promise<{ playlist: CoachingPlaylist }>;
  deletePlaylist: (id: number) => Promise<{ ok: true }>;
  isPending: boolean;
  error: ApiError | null;
};

export function useCoachPlaylistMutations(): CoachPlaylistMutations {
  const create = useMutation({
    mutationFn: createCoachingPlaylist,
    onSuccess: () => invalidateScope(),
  });
  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: UpdateCoachingPlaylistRequest;
    }) => updateCoachingPlaylist(id, body),
    onSuccess: () => invalidateScope(),
  });
  const remove = useMutation({
    mutationFn: deleteCoachingPlaylist,
    onSuccess: () => invalidateScope(),
  });

  const all = [create, update, remove];
  return {
    createPlaylist: (body) => create.mutateAsync(body),
    updatePlaylist: (id, body) => update.mutateAsync({ id, body }),
    deletePlaylist: (id) => remove.mutateAsync(id),
    isPending: all.some((m) => m.isPending),
    error: (all.find((m) => m.error)?.error as ApiError | undefined) ?? null,
  };
}
